import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MapPin, Lock, LogOut, Download, Users, Loader2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

ax.interceptors.response.use(null, async (err) => {
  if (err.response?.status === 401 && !err.config._retried && !err.config.url.includes("/auth/")) {
    err.config._retried = true;
    try {
      await ax.post("/auth/refresh");
      return ax(err.config);
    } catch (e) { /* fall through */ }
  }
  return Promise.reject(err);
});

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong");

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await ax.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 pt-16">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl bg-[#131620] border border-[#2A3042] p-8">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#FF453A]/10 border border-[#FF453A]/30 mx-auto mb-5">
          <Lock size={19} className="text-[#FF453A]" />
        </div>
        <h1 className="font-heading font-bold text-xl text-white text-center mb-6">Basecamp</h1>
        <input
          data-testid="admin-email-input"
          type="email" placeholder="Email" value={email} autoComplete="username"
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl bg-[#090A0F] border border-[#2A3042] focus:border-[#FF453A] outline-none text-white placeholder-[#5a6070] px-4 py-3 mb-3 transition-colors"
        />
        <input
          data-testid="admin-password-input"
          type="password" placeholder="Password" value={password} autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-xl bg-[#090A0F] border border-[#2A3042] focus:border-[#FF453A] outline-none text-white placeholder-[#5a6070] px-4 py-3 mb-5 transition-colors"
        />
        <button
          data-testid="admin-login-btn" type="submit" disabled={loading}
          className="w-full rounded-full bg-[#FF453A] hover:bg-[#FF6961] disabled:opacity-60 transition-colors text-white font-semibold py-3 flex items-center justify-center"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Enter"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const sendTestAlert = async () => {
    setSendingTest(true);
    try {
      await ax.post("/admin/test-alert");
      toast.success("Test text sent — check your phone.");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail) || "Test alert failed");
    } finally {
      setSendingTest(false);
    }
  };

  const loadEnrollments = useCallback(async () => {
    try {
      const { data } = await ax.get("/admin/enrollments");
      setData(data);
    } catch (err) {
      toast.error("Failed to load enrollments");
    }
  }, []);

  useEffect(() => {
    ax.get("/auth/me")
      .then(({ data }) => setAdmin(data))
      .catch(() => setAdmin(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (admin) loadEnrollments();
  }, [admin, loadEnrollments]);

  const logout = async () => {
    await ax.post("/auth/logout");
    setAdmin(false);
    setData(null);
  };

  const exportCsv = () => {
    const rows = [["Position", "Name", "Phone", "Email", "Interest", "Enrolled At"]];
    data.enrollments.forEach((e) =>
      rows.push([e.position, e.name, e.phone, e.email || "", e.interest || "", e.created_at])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "pinquests-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-[#FF453A]" />
      </div>
    );
  }

  if (!admin) return <LoginForm onSuccess={setAdmin} />;

  return (
    <main data-testid="admin-dashboard" className="pt-24 pb-20 min-h-screen">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-2">Basecamp</p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-white">Early Access List</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-test-alert-btn" onClick={sendTestAlert} disabled={sendingTest}
              className="rounded-full border border-[#2A3042] hover:border-[#34C759] transition-colors text-[#c9cdd8] text-sm font-semibold px-5 py-2.5 flex items-center gap-2 disabled:opacity-40">
              {sendingTest ? <Loader2 size={15} className="animate-spin" /> : <MessageSquareText size={15} />} Send test text
            </button>
            <button data-testid="admin-export-btn" onClick={exportCsv} disabled={!data?.count}
              className="rounded-full border border-[#2A3042] hover:border-[#FF453A] transition-colors text-[#c9cdd8] text-sm font-semibold px-5 py-2.5 flex items-center gap-2 disabled:opacity-40">
              <Download size={15} /> Export CSV
            </button>
            <button data-testid="admin-logout-btn" onClick={logout}
              className="rounded-full border border-[#2A3042] hover:border-[#FF453A] transition-colors text-[#9BA1B0] text-sm font-semibold px-5 py-2.5 flex items-center gap-2">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>

        <div className="rounded-2xl bg-[#131620] border border-[#2A3042] p-6 mb-8 flex items-center gap-4 max-w-xs">
          <div className="w-11 h-11 rounded-full bg-[#34C759]/10 border border-[#34C759]/30 flex items-center justify-center">
            <Users size={18} className="text-[#34C759]" />
          </div>
          <div>
            <p data-testid="admin-enroll-count" className="font-heading font-extrabold text-3xl text-white">{data?.count ?? "—"}</p>
            <p className="text-xs text-[#9BA1B0]">explorers enrolled</p>
          </div>
        </div>

        <div className="rounded-2xl bg-[#131620] border border-[#2A3042] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-enrollments-table">
            <thead>
              <tr className="border-b border-[#2A3042] text-left">
                {["#", "Name", "Phone", "Email", "Interest", "Enrolled"].map((h) => (
                  <th key={h} className="px-5 py-4 text-xs uppercase tracking-[0.15em] font-bold text-[#9BA1B0]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.enrollments?.length ? (
                data.enrollments.map((e) => (
                  <tr key={e.id} className="border-b border-[#2A3042]/50 hover:bg-[#090A0F]/50 transition-colors">
                    <td className="px-5 py-4 font-heading font-bold text-[#FF453A]">{e.position}</td>
                    <td className="px-5 py-4 text-white font-medium">{e.name}</td>
                    <td className="px-5 py-4 text-[#c9cdd8]">{e.phone}</td>
                    <td className="px-5 py-4 text-[#9BA1B0]">{e.email || "—"}</td>
                    <td className="px-5 py-4 text-[#9BA1B0]">{e.interest || "—"}</td>
                    <td className="px-5 py-4 text-[#9BA1B0]">{e.created_at?.slice(0, 10)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-[#5a6070]">
                    <MapPin size={20} className="mx-auto mb-2" /> No explorers yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
